import * as i0 from '@angular/core';
import { PLATFORM_ID, Component, Inject, ViewChild, Input } from '@angular/core';
import * as i1 from '@angular/common';
import { isPlatformBrowser, CommonModule } from '@angular/common';

class NgxHaloComponent {
    platformId;
    cdr;
    haloElement;
    circleElement;
    styleClass;
    position = "center";
    animate = true;
    interactive = true;
    set haloSize(size) {
        this.style["--om-halo-circle-size"] = size;
        this.updateHaloShadow(this.currentShadowColors);
    }
    set haloColors(colors) {
        this.style["--om-halo-circle-colors"] = colors;
    }
    set haloShadow(colors) {
        if (colors.length !== 3) {
            throw new Error("ShadowColors must contain exactly 3 colors!");
        }
        this.currentShadowColors = colors;
        this.updateHaloShadow(colors);
    }
    currentShadowColors = [
        "rgba(234, 255, 95, 0.8)",
        "rgba(244, 173, 108, 0.5)",
        "rgba(225, 71, 181, 0.6)",
    ];
    style = {};
    targetX = 0;
    targetY = 0;
    currentX = 0;
    currentY = 0;
    animationFrameId = null;
    intersectionObserver;
    isInView = false;
    constructor(platformId, cdr) {
        this.platformId = platformId;
        this.cdr = cdr;
    }
    ngAfterViewInit() {
        if (isPlatformBrowser(this.platformId)) {
            this.setupIntersectionObserver();
            if (this.interactive) {
                this.setupEventListeners();
            }
        }
    }
    ngOnDestroy() {
        this.intersectionObserver?.disconnect();
        this.removeEventListeners();
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }
    setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
            const wasInView = this.isInView;
            this.isInView = entry.isIntersecting;
            if (wasInView !== this.isInView) {
                this.cdr.detectChanges();
            }
        });
        this.intersectionObserver.observe(this.haloElement.nativeElement);
    }
    setupEventListeners() {
        const element = this.haloElement.nativeElement;
        element.addEventListener("mousemove", this.onMouseMove);
        element.addEventListener("mouseenter", this.onMouseEnter);
        element.addEventListener("mouseleave", this.onMouseLeave);
    }
    removeEventListeners() {
        const element = this.haloElement.nativeElement;
        element.removeEventListener("mousemove", this.onMouseMove);
        element.removeEventListener("mouseenter", this.onMouseEnter);
        element.removeEventListener("mouseleave", this.onMouseLeave);
    }
    onMouseMove = (event) => {
        const rect = this.haloElement.nativeElement.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        this.targetX = event.clientX - rect.left - centerX;
        this.targetY = event.clientY - rect.top - centerY;
        this.circleElement.nativeElement.classList.remove("animate");
        if (!this.animationFrameId) {
            this.animateCircle();
        }
    };
    animateCircle() {
        const delayFactor = 0.03;
        this.currentX += (this.targetX - this.currentX) * delayFactor;
        this.currentY += (this.targetY - this.currentY) * delayFactor;
        this.haloElement.nativeElement.style.setProperty("--halo-x-offset", `${this.currentX / 10 + 50}%`);
        this.haloElement.nativeElement.style.setProperty("--halo-y-offset", `${this.currentY / 10 + 50}%`);
        this.circleElement.nativeElement.style.transform = `translate(${this.currentX / 10}px, ${this.currentY / 10}px)`;
        this.animationFrameId = requestAnimationFrame(() => this.animateCircle());
    }
    onMouseEnter = () => {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
    };
    onMouseLeave = () => {
        this.returnToCenter();
    };
    returnToCenter() {
        const returnToCenterAnimation = () => {
            this.targetX = 0;
            this.targetY = 0;
            this.currentX += (this.targetX - this.currentX) * 0.02;
            this.currentY += (this.targetY - this.currentY) * 0.02;
            this.haloElement.nativeElement.style.setProperty("--halo-x-offset", `${this.currentX / 10 + 50}%`);
            this.haloElement.nativeElement.style.setProperty("--halo-y-offset", `${this.currentY / 10 + 50}%`);
            this.circleElement.nativeElement.style.transform = `translate(${this.currentX / 10}px, ${this.currentY / 10}px)`;
            if (Math.abs(this.currentX) < 0.1 && Math.abs(this.currentY) < 0.1) {
                cancelAnimationFrame(this.animationFrameId);
                this.animationFrameId = null;
                this.currentX = 0;
                this.currentY = 0;
                if (this.animate && this.isInView) {
                    this.circleElement.nativeElement.classList.add("animate");
                }
                return;
            }
            this.animationFrameId = requestAnimationFrame(returnToCenterAnimation);
        };
        cancelAnimationFrame(this.animationFrameId);
        returnToCenterAnimation();
    }
    updateHaloShadow(colors) {
        const numericSize = parseInt(this.style["--om-halo-circle-size"].replace("px", ""), 10) ||
            300;
        const offset1 = (numericSize * 60) / 300;
        const offset2 = (numericSize * 80) / 300;
        const offset3 = (numericSize * 120) / 300;
        this.style["--om-halo-circle-shadow"] = `
        ${-offset1}px ${-offset1}px ${offset1}px ${colors[0]},
        0 0 ${offset2}px ${colors[1]},
        ${offset3}px ${offset3}px ${offset3}px ${colors[2]}
    `;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: NgxHaloComponent, deps: [{ token: PLATFORM_ID }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.0", type: NgxHaloComponent, isStandalone: true, selector: "om-halo", inputs: { styleClass: "styleClass", position: "position", animate: "animate", interactive: "interactive", haloSize: "haloSize", haloColors: "haloColors", haloShadow: ["haloShadowColors", "haloShadow"] }, viewQueries: [{ propertyName: "haloElement", first: true, predicate: ["OmHaloElement"], descendants: true }, { propertyName: "circleElement", first: true, predicate: ["OmHaloCircle"], descendants: true }], ngImport: i0, template: "<div class=\"om-halo\" [ngClass]=\"styleClass\" [ngStyle]=\"style\" #OmHaloElement>\n  <div class=\"om-halo-background\" [ngClass]=\"position\">\n    <div\n      class=\"om-halo-circle\"\n      [class.animate]=\"animate && isInView\"\n      #OmHaloCircle\n    ></div>\n  </div>\n  <ng-content></ng-content>\n</div>\n", styles: [".om-halo{--om-halo-circle-size: 300px;--halo-x-offset: 30%;--halo-y-offset: 30%;--om-halo-circle-colors: #5fffda 20%, #46a8ff 50%, #cc32f6 100%;--om-halo-circle-shadow: -60px -60px 100px rgba(234, 255, 95, .8), 0 0 80px rgba(244, 173, 108, .5), 60px 60px 120px rgba(225, 71, 181, .6);position:relative;display:flex;justify-content:center;align-items:center}.om-halo .om-halo-background{position:absolute;width:100%;height:100%;display:flex}.om-halo .om-halo-background .om-halo-circle{width:var(--om-halo-circle-size);height:var(--om-halo-circle-size);border-radius:50%;filter:blur(calc(var(--om-halo-circle-size) * 150 / 300));background:radial-gradient(circle at var(--halo-x-offset) var(--halo-y-offset),var(--om-halo-circle-colors));transition:background 1s ease-in-out;box-shadow:var(--om-halo-circle-shadow)}.om-halo .om-halo-background .om-halo-circle.animate{animation:haloMove 10s ease-in-out infinite alternate}.om-halo .om-halo-background.center{justify-content:center;align-items:center}.om-halo .om-halo-background.top-center{align-items:flex-start;justify-content:center}.om-halo .om-halo-background.top-left{align-items:flex-start;justify-content:flex-start}.om-halo .om-halo-background.top-right{align-items:flex-start;justify-content:flex-end}.om-halo .om-halo-background.bottom-center{align-items:flex-end;justify-content:center}.om-halo .om-halo-background.bottom-left{align-items:flex-end;justify-content:flex-start}.om-halo .om-halo-background.bottom-right{align-items:flex-end;justify-content:flex-end}.om-halo .om-halo-background.left-center{align-items:center;justify-content:flex-start}.om-halo .om-halo-background.right-center{align-items:center;justify-content:flex-end}@keyframes haloMove{0%{transform:translate(0)}25%{transform:translate(20px,-20px)}50%{transform:translate(-20px,20px)}75%{transform:translate(15px,-15px)}to{transform:translate(0)}}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.0", ngImport: i0, type: NgxHaloComponent, decorators: [{
            type: Component,
            args: [{ selector: "om-halo", standalone: true, imports: [CommonModule], template: "<div class=\"om-halo\" [ngClass]=\"styleClass\" [ngStyle]=\"style\" #OmHaloElement>\n  <div class=\"om-halo-background\" [ngClass]=\"position\">\n    <div\n      class=\"om-halo-circle\"\n      [class.animate]=\"animate && isInView\"\n      #OmHaloCircle\n    ></div>\n  </div>\n  <ng-content></ng-content>\n</div>\n", styles: [".om-halo{--om-halo-circle-size: 300px;--halo-x-offset: 30%;--halo-y-offset: 30%;--om-halo-circle-colors: #5fffda 20%, #46a8ff 50%, #cc32f6 100%;--om-halo-circle-shadow: -60px -60px 100px rgba(234, 255, 95, .8), 0 0 80px rgba(244, 173, 108, .5), 60px 60px 120px rgba(225, 71, 181, .6);position:relative;display:flex;justify-content:center;align-items:center}.om-halo .om-halo-background{position:absolute;width:100%;height:100%;display:flex}.om-halo .om-halo-background .om-halo-circle{width:var(--om-halo-circle-size);height:var(--om-halo-circle-size);border-radius:50%;filter:blur(calc(var(--om-halo-circle-size) * 150 / 300));background:radial-gradient(circle at var(--halo-x-offset) var(--halo-y-offset),var(--om-halo-circle-colors));transition:background 1s ease-in-out;box-shadow:var(--om-halo-circle-shadow)}.om-halo .om-halo-background .om-halo-circle.animate{animation:haloMove 10s ease-in-out infinite alternate}.om-halo .om-halo-background.center{justify-content:center;align-items:center}.om-halo .om-halo-background.top-center{align-items:flex-start;justify-content:center}.om-halo .om-halo-background.top-left{align-items:flex-start;justify-content:flex-start}.om-halo .om-halo-background.top-right{align-items:flex-start;justify-content:flex-end}.om-halo .om-halo-background.bottom-center{align-items:flex-end;justify-content:center}.om-halo .om-halo-background.bottom-left{align-items:flex-end;justify-content:flex-start}.om-halo .om-halo-background.bottom-right{align-items:flex-end;justify-content:flex-end}.om-halo .om-halo-background.left-center{align-items:center;justify-content:flex-start}.om-halo .om-halo-background.right-center{align-items:center;justify-content:flex-end}@keyframes haloMove{0%{transform:translate(0)}25%{transform:translate(20px,-20px)}50%{transform:translate(-20px,20px)}75%{transform:translate(15px,-15px)}to{transform:translate(0)}}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: i0.ChangeDetectorRef }], propDecorators: { haloElement: [{
                type: ViewChild,
                args: ["OmHaloElement", { static: false }]
            }], circleElement: [{
                type: ViewChild,
                args: ["OmHaloCircle", { static: false }]
            }], styleClass: [{
                type: Input
            }], position: [{
                type: Input
            }], animate: [{
                type: Input
            }], interactive: [{
                type: Input
            }], haloSize: [{
                type: Input,
                args: ["haloSize"]
            }], haloColors: [{
                type: Input,
                args: ["haloColors"]
            }], haloShadow: [{
                type: Input,
                args: ["haloShadowColors"]
            }] } });

/*
 * Public API Surface of ngx-halo
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxHaloComponent };
